import { gql } from '@apollo/client';

// ─── Asset Queries ──────────────────────────────────────────────────────────

export const GET_ASSETS = gql`
  query GetAssets(
    $search: String
    $categoryId: Int
    $subcategoryId: Int
    $locationId: Int
    $custody: AssetCustody
    $paysVat: Boolean
    $antiquity: AssetAntiquityRange
    $status: AssetStatusFilter
    $page: Int
    $pageSize: Int
  ) {
    assets(
      search: $search
      categoryId: $categoryId
      subcategoryId: $subcategoryId
      locationId: $locationId
      custody: $custody
      paysVat: $paysVat
      antiquity: $antiquity
      status: $status
      page: $page
      pageSize: $pageSize
    ) {
      items {
        id
        plateNumber
        serialNumber
        brand
        model
        categoryId
        subcategoryId
        locationId
        custody
        purchaseDate
        paysVat
        assignedToEmail
        photoUrl
        createdAt
        updatedAt
        disposedAt
        disposedByEmail
        disposalReason
        disposalNote
        assignedTo {
          email
          name
          isActive
          blocked
        }
        category {
          id
          name
          nameEn
        }
        subcategory {
          id
          name
        }
        location {
          id
          name
          nameEn
          group
        }
      }
      total
      page
      pageSize
    }
  }
`;

export const GET_ASSET = gql`
  query GetAsset($id: Int!) {
    asset(id: $id) {
      id
      plateNumber
      serialNumber
      brand
      model
      categoryId
      subcategoryId
      locationId
      custody
      purchaseDate
      paysVat
      assignedToEmail
      photoUrl
      createdByEmail
      updatedByEmail
      createdAt
      updatedAt
      disposedAt
      disposedByEmail
      disposalReason
      disposalNote
      assignedTo {
        email
        name
        isActive
        blocked
      }
      category {
        id
        name
        nameEn
      }
      subcategory {
        id
        name
      }
      location {
        id
        name
        nameEn
        group
      }
    }
  }
`;

export const GET_ASSET_SEARCH_SUGGESTIONS = gql`
  query GetAssetSearchSuggestions($query: String!) {
    assetSearchSuggestions(query: $query) {
      id
      plateNumber
      brand
      model
      custody
      categoryName
      locationName
      assignedToName
    }
  }
`;

export const GET_ASSET_ASSIGNABLE_USERS = gql`
  query GetAssetAssignableUsers($search: String, $limit: Int) {
    assetAssignableUsers(search: $search, limit: $limit) {
      email
      name
      isActive
      blocked
    }
  }
`;

export const GET_ASSET_CATEGORIES = gql`
  query GetAssetCategories {
    assetCategories {
      id
      name
      nameEn
      isActive
      subcategories {
        id
        name
        categoryId
        isActive
      }
    }
  }
`;

export const GET_ASSET_CATEGORIES_ADMIN = gql`
  query GetAssetCategoriesAdmin {
    assetCategoriesAdmin {
      id
      name
      nameEn
      isActive
      subcategories {
        id
        name
        categoryId
        isActive
      }
    }
  }
`;

export const GET_ASSET_SUBCATEGORIES = gql`
  query GetAssetSubcategories($categoryId: Int!) {
    assetSubcategories(categoryId: $categoryId) {
      id
      name
      categoryId
      isActive
    }
  }
`;

export const GET_ASSET_LOCATIONS = gql`
  query GetAssetLocations {
    assetLocations {
      id
      name
      nameEn
      group
      isActive
      sortOrder
    }
  }
`;

export const GET_ASSET_LOCATIONS_WITH_COUNTS = gql`
  query GetAssetLocationsWithCounts {
    assetLocationsWithCounts {
      id
      name
      nameEn
      group
      isActive
      sortOrder
      assetCount
    }
  }
`;

export const GET_ASSET_HISTORY = gql`
  query GetAssetHistory($assetId: Int!) {
    assetHistory(assetId: $assetId) {
      id
      assetId
      action
      field
      oldValue
      newValue
      changedByEmail
      changedAt
    }
  }
`;

// ─── User Role Queries ──────────────────────────────────────────────────────

export const GET_MY_ROLES = gql`
  query GetMyRoles {
    myRoles
  }
`;

export const GET_USER_ROLES = gql`
  query GetUserRoles {
    userRoles {
      id
      userEmail
      role
      createdAt
      createdByEmail
    }
  }
`;

export const GET_USER_ROLES_BY_EMAIL = gql`
  query GetUserRolesByEmail($email: String!) {
    userRolesByEmail(email: $email) {
      id
      userEmail
      role
      createdAt
      createdByEmail
    }
  }
`;

// ─── Inventory audit ────────────────────────────────────────────────────────

const AUDIT_LOCATION_FIELDS = `
  id
  name
  nameEn
  group
  isActive
  sortOrder
`;

export const ASSET_AUDIT_FIELDS = `
  id
  locationId
  location { ${AUDIT_LOCATION_FIELDS} }
  note
  startedByEmail
  startedAt
  closedAt
  closedByEmail
  counts {
    scanned
    found
    misplaced
    unknown
    disposed
    duplicate
    expected
    missing
  }
`;

export const ASSET_AUDIT_ASSET_FIELDS = `
  id
  plateNumber
  brand
  model
  locationId
  custody
  disposedAt
  category { id name nameEn }
  location { ${AUDIT_LOCATION_FIELDS} }
`;

export const ASSET_AUDIT_SCAN_FIELDS = `
  id
  auditId
  code
  assetId
  result
  assetLocationIdAtScan
  locationAtScan { ${AUDIT_LOCATION_FIELDS} }
  scannedByEmail
  scannedAt
  asset { ${ASSET_AUDIT_ASSET_FIELDS} }
`;

export const GET_ASSET_AUDITS = gql`
  query GetAssetAudits {
    assetAudits {
      ${ASSET_AUDIT_FIELDS}
    }
  }
`;

export const GET_ASSET_AUDIT = gql`
  query GetAssetAudit($id: Int!) {
    assetAudit(id: $id) {
      audit {
        ${ASSET_AUDIT_FIELDS}
      }
      scans {
        ${ASSET_AUDIT_SCAN_FIELDS}
      }
      missing {
        asset { ${ASSET_AUDIT_ASSET_FIELDS} }
        expectedLocation { ${AUDIT_LOCATION_FIELDS} }
      }
    }
  }
`;

/** Problems reported at desks; open ones oldest first. */
export const GET_WORKSTATION_ISSUES = gql`
  query WorkstationIssues($open: Boolean!) {
    workstationIssues(open: $open) {
      id
      tableId
      workstationId
      message
      reportedByEmail
      reportedByName
      reportedAt
      resolvedAt
      resolvedByEmail
      asset {
        id
        categoryName
        categoryNameEn
        brand
        model
      }
    }
  }
`;
